/**
 * HP Bar Render Helper (PIXI)
 *
 * Pure render helper that draws an HP bar onto a PIXI.Graphics object,
 * with an optional GM-only `${current}/${max}` text overlay.
 *
 * Spec: specs/2026-05-14-vtt-token-hp-bar.md (iter-2)
 *
 * Why a helper, not a React component:
 *   VTTCanvas is PIXI.js, not raw 2D and not React-in-canvas. The bar lives in
 *   the same parent container as the token sprite so it inherits pan/zoom for free.
 *
 * Caller responsibilities:
 *   - Create / reuse a PIXI.Graphics instance and (optionally) a PIXI.Text.
 *   - Add them to the token's container at the desired offset.
 *   - Call `drawHPBar` to (re)draw on each render.
 *   - Decide whether to render at all per the spec's "Conditions to render"
 *     (characterId set, character exists, hp.max > 0 finite, visibility).
 *     This helper assumes the caller already passed those gates and just paints.
 *
 * Guards inside the helper:
 *   - `Number.isFinite(max) && max > 0` — defensive: returns without drawing
 *     anything if the caller slips a bad max through. Avoids `NaN`-width fills.
 *   - Ratio is clamped to [0, 1] before width math.
 */
import * as PIXI from 'pixi.js'

export interface DrawHPBarOpts {
  /** Pixel width of the bar (typically token.width * map.gridSize). */
  widthPx: number
  /** Current HP. Will be clamped 0..max for the fill ratio. */
  current: number
  /** Maximum HP. Bar is suppressed if not a finite positive number. */
  max: number
  /** When true, draw `${current}/${max}` text overlay (GM only per spec). */
  showText: boolean
}

/** Bar height in pixels (spec: 6). */
export const HP_BAR_HEIGHT = 6

/**
 * Whether the HP bar should be drawn at all for a given viewer.
 *  - An owner who has opted out of seeing their OWN token's HP bar (VTT #526,
 *    `ownerHidSelf`) never sees that token's bar — this is a private, client-
 *    side view preference and wins over every other rule for that viewer.
 *  - The GM always sees the bar.
 *  - The token's owner always sees their own bar, regardless of the GM toggle —
 *    "I can control it implies I can see it" (mirrors the ownership override
 *    in tokenVisibility.ts).
 *  - Any other player sees it unless the token's `hpBarVisibleToPlayers` is
 *    explicitly `false` (undefined defaults to visible, preserving prior
 *    behavior for existing tokens).
 *
 * `ownerHidSelf` only has meaning when `isOwner` is true; callers pass the
 * owner's per-token preference (false for non-owners).
 *
 * This is the per-viewer visibility gate only — it does NOT cover the HP/max
 * sanity gates (finite positive max, etc.), which the caller applies
 * separately.
 */
export function shouldShowHPBar(
  isGM: boolean,
  isOwner: boolean,
  hpBarVisibleToPlayers: boolean | undefined,
  ownerHidSelf = false
): boolean {
  // #526: the owner's private opt-out beats every other rule for this viewer.
  if (isOwner && ownerHidSelf) return false
  if (isGM) return true
  if (isOwner) return true
  return hpBarVisibleToPlayers !== false
}

/** Background fill color (dark grey at low alpha). */
const BG_COLOR = 0x222222
const BG_ALPHA = 0.6

/** Fill colors per spec thresholds. */
const COLOR_GREEN = 0x4caf50
const COLOR_AMBER = 0xffb300
const COLOR_RED = 0xe53935

/** Choose fill color from clamped ratio per spec thresholds. */
function colorForRatio(ratio: number): number {
  if (ratio >= 0.5) return COLOR_GREEN
  if (ratio >= 0.2) return COLOR_AMBER
  return COLOR_RED
}

/**
 * Draws / redraws an HP bar onto `graphics`. If `text` is provided and
 * `opts.showText` is true, formats it as `${current}/${max}` and centers it
 * on the bar. Otherwise hides the text.
 *
 * The graphics is `clear()`-ed first so this is safe to call repeatedly on
 * the same instance (e.g., on every animation tick or character update).
 */
export function drawHPBar(
  graphics: PIXI.Graphics,
  text: PIXI.Text | null,
  opts: DrawHPBarOpts
): void {
  const { widthPx, current, max, showText } = opts

  // Always start clean — caller may be reusing this graphics across frames.
  graphics.clear()

  // Defensive max guard (the caller should also gate, per spec). If max is
  // 0/negative/NaN/Infinite, draw nothing — not even an empty bar.
  if (!Number.isFinite(max) || max <= 0) {
    if (text) text.visible = false
    return
  }

  // Clamp ratio to [0, 1] so over-heal or negative values don't break layout.
  const safeCurrent = Number.isFinite(current) ? current : 0
  const ratio = Math.max(0, Math.min(1, safeCurrent / max))
  const fillWidth = widthPx * ratio

  // Background bar (full width, dark grey, semi-transparent).
  graphics.beginFill(BG_COLOR, BG_ALPHA)
  graphics.drawRect(0, 0, widthPx, HP_BAR_HEIGHT)
  graphics.endFill()

  // Foreground fill — only if there's any width to draw.
  if (fillWidth > 0) {
    graphics.beginFill(colorForRatio(ratio))
    graphics.drawRect(0, 0, fillWidth, HP_BAR_HEIGHT)
    graphics.endFill()
  }

  // Optional GM-only text overlay: `${current}/${max}` centered on the bar.
  if (text) {
    if (showText) {
      text.text = `${current}/${max}`
      text.anchor.set(0.5)
      text.x = widthPx / 2
      text.y = HP_BAR_HEIGHT / 2
      text.visible = true
    } else {
      text.visible = false
    }
  }
}

/**
 * Convenience factory for the GM text overlay. Caller still owns lifecycle
 * (must `addChild` to the token container and `destroy` on cleanup).
 *
 * Style: 10px sans-serif, white fill, 1px black stroke (per spec).
 */
export function createHPBarText(): PIXI.Text {
  return new PIXI.Text('', {
    fontFamily: 'sans-serif',
    fontSize: 10,
    fill: 0xffffff,
    stroke: 0x000000,
    strokeThickness: 1,
    align: 'center',
  })
}

// ---------------------------------------------------------------------------
// HP Pill — new design (Figma node 3242-425)
// ---------------------------------------------------------------------------

/** HP pill dimensions (design spec). */
export const HP_PILL_HEIGHT = 16
export const HP_PILL_WIDTH = 50
export const HP_PILL_RADIUS = 4

/** HP pill fill: Grimoire green #067100 @ 75 % opacity. */
export const HP_PILL_COLOR = 0x067100
export const HP_PILL_ALPHA = 0.75

/**
 * Draws (or redraws) a green rounded-rect HP pill onto `graphics`.
 * `text` always receives `current/max` — the pill is always legible, not
 * GM-gated. Caller controls whether the pill's *container* is visible at all
 * (hover / selected gate applied by VTTCanvas).
 *
 * The existing `shouldShowHPBar` / `hpBarVisibleToPlayers` rules still
 * determine whether the pill is *created*; this helper only paints.
 */
export function drawHPPill(
  graphics: PIXI.Graphics,
  text: PIXI.Text,
  opts: { current: number; max: number }
): void {
  const { current, max } = opts
  graphics.clear()

  if (!Number.isFinite(max) || max <= 0) {
    text.visible = false
    return
  }

  graphics.beginFill(HP_PILL_COLOR, HP_PILL_ALPHA)
  graphics.drawRoundedRect(0, 0, HP_PILL_WIDTH, HP_PILL_HEIGHT, HP_PILL_RADIUS)
  graphics.endFill()

  text.text = `${Math.round(current)}/${max}`
  text.anchor.set(0.5)
  text.x = HP_PILL_WIDTH / 2
  text.y = HP_PILL_HEIGHT / 2
  text.visible = true
}

/**
 * Factory for the HP pill text label.
 * White, DM Sans 10 px, centered (no stroke — pill bg provides contrast).
 */
export function createHPPillText(): PIXI.Text {
  return new PIXI.Text('', {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 10,
    fill: 0xffffff,
    align: 'center',
  })
}
